/**
 * Engine HTTP client for the tags registry. Mirrors the routes mounted in
 * `services/engine/src/api/routes/tags.ts`:
 *
 *   GET  /api/tags        → Tag[]  (sorted by name)
 *   POST /api/tags        → Tag    (201, creates a new tag)
 *
 * Tags are tenant-global (no pipeline scope), so this client lives at
 * `lib/api/tags.ts` alongside `lib/api/contacts.ts`.
 */

import { z } from 'zod'
import type { Tag, TagId } from '@ludus/core'
import { apiClient } from './client.ts'
import { ApiError, classifyApiError } from './pipelines.ts'

const tagSchema = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string().nullable(),
})

function castTag(t: z.infer<typeof tagSchema>): Tag {
  return { ...t, id: t.id as TagId }
}

export async function listTags(): Promise<Tag[]> {
  try {
    const { data } = await apiClient.get('/tags')
    return z.array(tagSchema).parse(data).map(castTag)
  } catch (err) {
    throw classifyApiError(err)
  }
}

export async function createTag(name: string, color?: string): Promise<Tag> {
  try {
    const { data } = await apiClient.post('/tags', { name, color })
    return castTag(tagSchema.parse(data))
  } catch (err) {
    throw classifyApiError(err)
  }
}

export { ApiError }
